import { UserDto } from "../../DTOs/User/UserDto";
import i18n, { currentLanguage } from "../../i18n";
import { roleLabel, viewName } from "../../i18n/pageNames";
import { ViewKey } from "./pageVocabulary.generated";
import { roleName } from "./roleUtilities";
import { pageOf, RouteRequirements, satisfiesRouteRequirements } from "./routeAccess";

export interface AccessChange {
  role: { name: string | null } | null;
  zones: string[] | null;
  lostPage: ViewKey | null;
}

const sameMembers = <T,>(a: readonly T[], b: readonly T[]) =>
  a.length === b.length && a.every(value => b.includes(value));

const zoneIds = (user: UserDto) => user.zones.map(zone => zone.zoneId);

const contractorZoneIds = (user: UserDto) =>
  user.zones.filter(zone => zone.isSubcontractor).map(zone => zone.zoneId);

const privilegeChanged = (before: UserDto, after: UserDto) =>
  before.isAdmin !== after.isAdmin
  || before.hasLeadAdminRights !== after.hasLeadAdminRights
  || before.isSuperAdmin !== after.isSuperAdmin;

const canOpen = (route: RouteRequirements[], user: UserDto) =>
  route.every(level => satisfiesRouteRequirements(level, user));

export function accessChange(
  before: UserDto | null,
  after: UserDto | null,
  openRoute: RouteRequirements[] = [],
): AccessChange | null {
  if (!before || !after || before.id !== after.id) return null;

  const roleChanged = privilegeChanged(before, after);
  const zonesChanged = !sameMembers(zoneIds(before), zoneIds(after));
  const changed = roleChanged
    || zonesChanged
    || !sameMembers(before.areaKeys, after.areaKeys)
    || !sameMembers(before.visibleTileKeys ?? [], after.visibleTileKeys ?? [])
    || !sameMembers(contractorZoneIds(before), contractorZoneIds(after));
  if (!changed) return null;

  const lostPage = canOpen(openRoute, before) && !canOpen(openRoute, after)
    ? pageOf(openRoute)?.view ?? null
    : null;

  return {
    role: roleChanged ? { name: roleName(after) } : null,
    zones: zonesChanged && !after.isSuperAdmin ? after.zones.map(zone => zone.zone) : null,
    lostPage,
  };
}

const listFormat = {
  format: (items: string[]) => {
    const language = currentLanguage();
    return new Intl.ListFormat(language === "en" ? "en-GB" : language, { type: "conjunction" }).format(items);
  },
};

function headline({ role, zones }: AccessChange): string {
  const newRole = role?.name ? roleLabel(role.name) : null;

  if (role && zones) {
    if (zones.length) {
      const values = { role: newRole, zones: listFormat.format(zones) };
      return newRole ? i18n.t("accessChange.roleNowWithZones", values) : i18n.t("accessChange.roleChangedWithZones", values);
    }
    return newRole ? i18n.t("accessChange.roleNowNoZones", { role: newRole }) : i18n.t("accessChange.roleChangedNoZones");
  }
  if (role) return newRole ? i18n.t("accessChange.roleNow", { role: newRole }) : i18n.t("accessChange.roleChanged");
  if (zones) {
    return zones.length ? i18n.t("accessChange.zonesNow", { zones: listFormat.format(zones) }) : i18n.t("accessChange.noZones");
  }

  return i18n.t("accessChange.updated");
}

export function describeAccessChange(change: AccessChange): string {
  return [
    headline(change),
    change.lostPage && i18n.t("accessChange.lostPage", { page: viewName(change.lostPage) }),
    change.role && i18n.t("accessChange.signInAgain"),
  ].filter(Boolean).join(" ");
}
